// In-call layout (design "Meeting room"): top bar with title/code + the two
// trust pills (E2EE badge F-SEC-1, REC F-REC-1 — unmissable, §16.3), stage
// with grid or spotlight+filmstrip, one side panel at a time, control bar,
// knock toast, floating reactions. Subscribes only to identity lists, layout,
// and flags — per-attendee churn stays inside the memoized tiles.

import { memo, useCallback, useEffect, useState } from 'react';

import { api } from '../api';
import { probeRender } from '../perf/renderProbe';
import { watchHostQueue } from '../realtime';
import {
  clearKnocks,
  pushToast,
  reconcileKnocks,
  removeKnock,
  upsertKnock,
  useCallStore,
  useCallStoreShallow,
} from '../store/callStore';
// Importing pollStore/timerStore also loads their engine subscriptions so the
// host coordinates polls/timer whether or not the Meeting Tools panel is open.
import { resetPolls } from '../store/pollStore';
import { resetTimerState } from '../store/timerStore';
import { TimerFlash } from './TimerFlash';
import { engine } from '../engine';
import { onDesktopPushToTalk } from '../desktop';
import { ChatPanel } from './ChatPanel';
import { Elapsed } from './ControlsBar';
import { ControlsBar } from './ControlsBar';
import { TimerPill } from './TimerPill';
import { EncryptionBadge } from './EncryptionBadge';
import { FilesPanel } from './FilesPanel';
import { HostMenu } from './HostMenu';
import { MeetingToolsPanel } from './MeetingToolsPanel';
import { MeetingReadyCard } from './MeetingReadyCard';
import { KnockToast } from './KnockToast';
import { ParticipantTile } from './ParticipantTile';
import { PeoplePanel } from './PeoplePanel';
import { ReactionsOverlay } from './ReactionsOverlay';
import { RecordingIndicator } from './RecordingIndicator';
import { RemoteAudio } from './RemoteAudio';

export type PanelKind = 'chat' | 'people' | 'files' | 'tools' | 'host' | null;

/** Wall clock in the top bar; its 30s tick re-renders only this span. */
const Clock = memo(function Clock() {
  const [label, setLabel] = useState(() =>
    new Date().toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
  );
  useEffect(() => {
    const t = setInterval(
      () => setLabel(new Date().toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })),
      30_000,
    );
    return () => clearInterval(t);
  }, []);
  return <span className="bz-call-clock bz-hide-mobile">{label}</span>;
});

export function CallView({
  slug,
  title,
  readyCard,
}: {
  slug: string;
  title: string | null;
  /** Set only when the host just created this instant meeting — shows the
   * one-time "meeting's ready" share card over the call. */
  readyCard?: { joinedAs: string } | null;
}) {
  probeRender('CallView');
  const [readyDismissed, setReadyDismissed] = useState(false);
  const dismissReady = useCallback(() => setReadyDismissed(true), []);
  const layout = useCallStore((s) => s.layout);
  const order = useCallStoreShallow((s) => s.order);
  const presenterId = useCallStoreShallow(
    (s) => s.order.find((id) => s.attendees[id]?.screenShareEnabled) ?? null,
  );
  const pinned = useCallStore((s) => s.pinnedIdentity !== null);
  const roomState = useCallStore((s) => s.roomState);
  const isHost = useCallStore((s) => s.isHost);
  const [panel, setPanel] = useState<PanelKind>(null);

  // Poll + timer state is per-meeting: clear it on entering and leaving the call.
  useEffect(() => {
    resetPolls();
    resetTimerState();
    return () => {
      resetPolls();
      resetTimerState();
    };
  }, []);

  const togglePanel = useCallback(
    (kind: Exclude<PanelKind, null>) => setPanel((p) => (p === kind ? null : kind)),
    [],
  );
  const closePanel = useCallback(() => setPanel(null), []);
  // Mobile: the chat entry lives in the People sheet (the design's 5-button bar).
  const openChatFromPeople = useCallback(() => setPanel('chat'), []);

  // Desktop global push-to-talk (§9.2): toggles the mic even unfocused.
  useEffect(() => {
    return onDesktopPushToTalk(() => {
      const me = engine.snapshots().find((a) => a.isLocal);
      void engine.setMicEnabled(!me?.micEnabled);
    });
  }, []);

  // Host: stream the admission queue for the lifetime of the call (F-LOB-2).
  useEffect(() => {
    if (!isHost) return;
    const known = new Set<string>();
    const stop = watchHostQueue(slug, {
      onKnock: (k) => {
        upsertKnock(k);
        if (!known.has(k.id)) {
          known.add(k.id);
          pushToast(`${k.name} wants to join`);
        }
      },
      onKnockGone: (id) => removeKnock(id),
    });
    // Polling backstop: the WS is best-effort, so reconcile against the
    // authoritative server queue every 5s — the host never misses a knock even
    // if the socket is degraded (toast once per newly-seen knock).
    const poll = window.setInterval(() => {
      void api
        .admissionQueue(slug)
        .then((list) => {
          reconcileKnocks(list);
          for (const k of list) {
            if (!known.has(k.id)) {
              known.add(k.id);
              pushToast(`${k.name} wants to join`);
            }
          }
        })
        .catch(() => {});
    }, 5000);
    return () => {
      stop();
      window.clearInterval(poll);
      clearKnocks();
    };
  }, [isHost, slug]);

  return (
    <div className="bz-call">
      {roomState === 'reconnecting' && (
        <div className="bz-reconnect-banner" role="status">
          Connection lost — trying to reconnect…
        </div>
      )}
      <div className="bz-call-topbar">
        <div className="bz-call-topbar-left">
          <span className="bz-call-title">{title ?? 'Meeting'}</span>
          <span className="bz-call-code">{slug}</span>
          <EncryptionBadge />
          <RecordingIndicator />
        </div>
        <div className="bz-call-topbar-right">
          <TimerPill />
          <Clock />
          <Elapsed className="bz-call-clock bz-mobile-only" />
        </div>
      </div>
      <div className="bz-call-stage">
        <div className="bz-stage-main">
          {layout === 'speaker' || presenterId || pinned ? (
            <SpeakerLayout order={order} presenterId={presenterId} />
          ) : (
            <div className="bz-grid" data-count={Math.min(order.length, 9)}>
              {order.map((id) => (
                <ParticipantTile key={id} identity={id} source="camera" />
              ))}
            </div>
          )}
          <ReactionsOverlay />
          <TimerFlash />
        </div>
        {panel === 'chat' && <ChatPanel onClose={closePanel} title={title} />}
        {panel === 'people' && (
          <PeoplePanel slug={slug} onClose={closePanel} onOpenChat={openChatFromPeople} />
        )}
        {panel === 'files' && <FilesPanel onClose={closePanel} />}
        {panel === 'host' && isHost && <HostMenu slug={slug} onClose={closePanel} />}
        {panel === 'tools' && <MeetingToolsPanel onClose={closePanel} />}
      </div>
      <RemoteAudio />
      <ControlsBar slug={slug} panel={panel} onTogglePanel={togglePanel} />
      {isHost && <KnockToast slug={slug} />}
      {readyCard && !readyDismissed && (
        <MeetingReadyCard slug={slug} joinedAs={readyCard.joinedAs} onClose={dismissReady} />
      )}
    </div>
  );
}

function SpeakerLayout({ order, presenterId }: { order: string[]; presenterId: string | null }) {
  probeRender('SpeakerLayout');
  // Main stage priority: a pinned attendee (F-CALL-4) > screen share >
  // current active speaker > first remote > self.
  const pinnedIdentity = useCallStore((s) => s.pinnedIdentity);
  const speakerId = useCallStore((s) => {
    if (s.pinnedIdentity && s.attendees[s.pinnedIdentity]) return s.pinnedIdentity;
    if (presenterId) return presenterId;
    const speaking = s.order.find((id) => s.attendees[id]?.isSpeaking && !s.attendees[id]?.isLocal);
    return speaking ?? s.order.find((id) => !s.attendees[id]?.isLocal) ?? s.order[0] ?? null;
  });

  if (!speakerId) return null;
  // A pinned camera overrides the share stage; otherwise the share wins.
  const stageSource = pinnedIdentity === speakerId ? 'camera' : presenterId ? 'screen' : 'camera';
  return (
    <div className="bz-speaker-layout">
      <div className="bz-stage">
        <ParticipantTile identity={speakerId} source={stageSource} />
      </div>
      <div className="bz-thumbs">
        {order.map((id) => (
          <ParticipantTile key={id} identity={id} source="camera" />
        ))}
      </div>
    </div>
  );
}
